import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { categoryLabel } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none";

export default function MessagePlayers({ tid, categories, regs = [] }) {
  const [f, setF] = useState({ subject: "", body: "", channel: "both", category_id: "", registration_id: "" });
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [log, setLog] = useState([]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const loadLog = () => api.get(`/tournaments/${tid}/messages`).then(({ data }) => setLog(data)).catch(() => {});
  useEffect(() => { loadLog(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setAsk(false); setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/message`, { ...f, category_id: f.category_id || null, registration_id: f.registration_id || null });
      toast.success(`Sent to ${data.recipients} players · ${data.emails} emails · ${data.inapp} in-app`);
      setF((s) => ({ ...s, subject: "", body: "" })); loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const target = f.registration_id ? regs.find((r) => r.registration_id === f.registration_id)?.player_name : f.category_id ? categoryLabel(categories.find((c) => c.id === f.category_id) || {}) : "all players";

  return (
    <div className="rounded-2xl glow-border p-5 space-y-3" style={{ background: "var(--surface)" }} data-testid="message-players">
      <div className="font-display font-bold flex items-center gap-2"><Mail className="w-4 h-4 text-primary-c" /> Message players</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select data-testid="msg-category" value={f.category_id} onChange={(e) => setF((s) => ({ ...s, category_id: e.target.value, registration_id: "" }))} className={inputCls} style={{ color: "var(--text)" }}>
          <option value="">All categories</option>{categories.map((c) => <option key={c.id} value={c.id}>{categoryLabel(c)}</option>)}
        </select>
        <select data-testid="msg-player" value={f.registration_id} onChange={set("registration_id")} className={inputCls} style={{ color: "var(--text)" }}>
          <option value="">All players{f.category_id ? " in category" : ""}</option>
          {regs.filter((r) => !f.category_id || r.category_id === f.category_id).filter((r) => r.payment_status !== "rejected").map((r) => <option key={r.registration_id} value={r.registration_id}>{r.player_name}{r.partner?.name ? ` / ${r.partner.name}` : ""}</option>)}
        </select>
        <select data-testid="msg-channel" value={f.channel} onChange={set("channel")} className={inputCls} style={{ color: "var(--text)" }}>
          <option value="both">Email + in-app</option><option value="email">Email only</option><option value="inapp">In-app only</option>
        </select>
      </div>
      <input data-testid="msg-subject" value={f.subject} onChange={set("subject")} placeholder="Subject" maxLength={150} className={inputCls} style={{ color: "var(--text)" }} />
      <textarea data-testid="msg-body" value={f.body} onChange={set("body")} rows={4} maxLength={5000} placeholder="Your message to players…" className={inputCls} style={{ color: "var(--text)" }} />
      <button data-testid="msg-send-btn" disabled={busy || f.subject.trim().length < 2 || f.body.trim().length < 2} onClick={() => setAsk(true)} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
      </button>
      {log.length > 0 && (
        <details data-testid="msg-log"><summary className="text-xs font-bold cursor-pointer" style={{ color: "var(--text-2)" }}>Sent messages ({log.length})</summary>
          {log.map((m) => <div key={m.message_id} className="text-xs py-1.5 border-b last:border-0 break-words" style={{ borderColor: "var(--border)" }}><b>{m.subject}</b> · {m.recipients} players · {m.channel} · <span className="mono">{m.created_at.slice(0, 16).replace("T", " ")}</span></div>)}
        </details>
      )}
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="msg-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display">Send this message?</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>"{f.subject}" will be sent to {target} ({f.channel === "both" ? "email + in-app" : f.channel === "email" ? "email" : "in-app"}).</DialogDescription></DialogHeader>
          <DialogFooter>
            <button data-testid="msg-confirm-cancel" onClick={() => setAsk(false)} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="msg-confirm-yes" onClick={send} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold">Yes, send</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
